import { DriverModeTO } from '@control-center/control-center-client-public-domain-simulation-ts';
import { zodResolver } from '@hookform/resolvers/zod';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import Input from 'components/forms/Input';
import RadioButton from 'components/forms/RadioButton';
import FormFieldSelect from 'components/forms/Select';
import type { Scenario } from 'features/simulations-v2/types';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { managementApi } from './apiClient';

const schema = z.object({
    vin: z.string().regex(/^[A-Z0-9]{17}$/, 'Must be exactly 17 uppercase alphanumeric characters'),
    scenarioId: z.string().min(1, 'Please select a scenario'),
    driverMode: z.enum([DriverModeTO.DriverModeInSupervised, DriverModeTO.DriverModeOutAutomated]),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    scenarios: Scenario[];
    onCancel: () => void;
    onSuccess: () => void;
}

const CreateSimulationForm = ({ scenarios, onCancel, onSuccess }: Props) => {
    const [isError, setIsError] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { driverMode: DriverModeTO.DriverModeOutAutomated },
        mode: 'all',
    });

    const scenarioOptions = scenarios.map(s => ({ id: s.id, label: s.name }));

    const onSubmit = async (data: FormValues) => {
        setIsError(false);
        try {
            await managementApi.initializeSimulation(data.vin, {
                scenarioId: data.scenarioId,
                driverMode: data.driverMode,
            });
            onSuccess();
        } catch (error) {
            console.error(error);
            setIsError(true);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className='DomainSimulationCreateForm'>
                <div>
                    <Input
                        name='vin'
                        type='text'
                        label='VIN'
                        placeholder='e.g. SIM0000000000001A'
                        register={register}
                        error={errors.vin}
                    />
                    <p className='DomainSimulationCreateForm__hint'>
                        The VIN must be registered in Control Center as a simulation vehicle.
                    </p>
                </div>
                <FormFieldSelect
                    name='scenarioId'
                    type='text'
                    label='Scenario'
                    placeholder='Select a scenario'
                    options={scenarioOptions}
                    control={control}
                    error={errors.scenarioId}
                    onChange={() => {}}
                />
                <Controller
                    name='driverMode'
                    control={control}
                    render={({ field }) => (
                        <div className='form-group mb-0'>
                            <label>Driver Mode</label>
                            <div className='DomainSimulationCreateForm__driverModeOptions'>
                                <RadioButton
                                    name='driverMode'
                                    checked={field.value === DriverModeTO.DriverModeOutAutomated}
                                    onChange={() => field.onChange(DriverModeTO.DriverModeOutAutomated)}
                                >
                                    OUT (automated)
                                </RadioButton>
                                <RadioButton
                                    name='driverMode'
                                    checked={field.value === DriverModeTO.DriverModeInSupervised}
                                    onChange={() => field.onChange(DriverModeTO.DriverModeInSupervised)}
                                >
                                    IN (supervised)
                                </RadioButton>
                            </div>
                        </div>
                    )}
                />
                {isError && (
                    <p className='DomainSimulationCreateForm__error'>Failed to create simulation. Please try again.</p>
                )}
            </div>
            <FormDialogFooter
                onConfirmLabel='Create simulation'
                onCancel={onCancel}
                isValid={isValid}
                isLoading={isSubmitting}
            />
        </form>
    );
};

export default CreateSimulationForm;
